import { format as d3format } from 'd3-format'
import { timeFormat } from 'd3-time-format'
import { getTypeName } from '@rawgraphs/rawgraphs-core'
import { getAxisGroupNode } from './axisNodes'

export const AXIS_DATE_FORMAT_OPTIONS = [
  { id: 'auto', label: 'Automatic' },
  { id: 'year', label: 'YYYY' },
  { id: 'monthYear', label: 'Mon YYYY' },
  { id: 'monthDayYear', label: 'Mon D, YYYY' },
  { id: 'weekdayShort', label: 'Mon' },
  { id: 'weekdayMonthDay', label: 'Mon, Mon D' },
  { id: 'fullDate', label: 'Month D, YYYY' },
]

export const AXIS_NUMBER_FORMAT_OPTIONS = [
  { id: 'auto', label: 'Automatic' },
  { id: 'plain', label: 'Plain number' },
  { id: 'comma', label: 'Comma separated' },
  { id: 'financial', label: 'Financial (K / M / B / T)' },
  { id: 'abbrev', label: 'SI abbreviation (k / M / G / T)' },
]

const DATE_FORMATTERS = {
  year: timeFormat('%Y'),
  monthYear: timeFormat('%b %Y'),
  monthDayYear: timeFormat('%b %-d, %Y'),
  weekdayShort: timeFormat('%a'),
  weekdayMonthDay: timeFormat('%a, %b %-d'),
  fullDate: timeFormat('%B %-d, %Y'),
}

const FINANCIAL_TIERS = [
  [1e12, 'T'],
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K'],
]

function financialAbbrev(value) {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  for (const [threshold, suffix] of FINANCIAL_TIERS) {
    if (abs >= threshold) {
      const scaled = abs / threshold
      return sign + parseFloat(scaled.toPrecision(3)).toString() + suffix
    }
  }
  return String(value)
}

const NUMBER_FORMATTERS = {
  plain: d3format('d'),
  comma: d3format(','),
  financial: financialAbbrev,
  abbrev: d3format('~s'),
}

const X_AXIS_DIMENSION_IDS = ['x', 'bars', 'groups', 'stacks', 'sets', 'startDate']
const Y_AXIS_DIMENSION_IDS = ['y', 'size', 'series', 'endDate']

function getMappedColumn(mapping, dimensionIds = []) {
  for (const dimensionId of dimensionIds) {
    const column = mapping?.[dimensionId]?.value?.[0]
    if (column) {
      return { dimensionId, column }
    }
  }

  return null
}

function getAxisMeta(axis, mapping, dataTypes) {
  const mapped =
    axis === 'x'
      ? getMappedColumn(mapping, X_AXIS_DIMENSION_IDS)
      : getMappedColumn(mapping, Y_AXIS_DIMENSION_IDS)

  if (!mapped?.column || !dataTypes?.[mapped.column]) {
    return null
  }

  const typeName = getTypeName(dataTypes[mapped.column])
  if (typeName !== 'date' && typeName !== 'number') {
    return null
  }

  return {
    axis,
    column: mapped.column,
    kind: typeName,
  }
}

function normalizeDate(value) {
  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return value
  }

  const nextDate = new Date(value)
  return Number.isNaN(nextDate.valueOf()) ? null : nextDate
}

function detectAutoDateFormatId(tickValues) {
  const dates = tickValues.map(normalizeDate).filter(Boolean)
  if (dates.length < 2) return 'monthYear'
  const spanMs = Math.max(...dates) - Math.min(...dates)
  const spanDays = spanMs / 86400000
  if (spanDays > 365 * 2) return 'year'
  if (spanDays > 60) return 'monthYear'
  return 'monthDayYear'
}

function isYearLike(nums) {
  return nums.length > 0 && nums.every(n => Number.isInteger(n) && n >= 1000 && n <= 2200)
}

function detectAutoNumberFormatId(tickValues) {
  const nums = tickValues.map(Number).filter(Number.isFinite)
  if (!nums.length) return 'plain'
  if (isYearLike(nums)) return 'plain'
  const maxAbs = Math.max(...nums.map(Math.abs))
  if (maxAbs >= 1e6) return 'financial'
  if (maxAbs >= 1000) return 'comma'
  return 'plain'
}

function formatTickValue(value, axisMeta, formatId, resolvedAutoId = null) {
  if (!axisMeta || !formatId) return null

  const effectiveId = formatId === 'auto' ? resolvedAutoId : formatId
  if (!effectiveId || effectiveId === 'auto') return null

  if (axisMeta.kind === 'date') {
    const formatter = DATE_FORMATTERS[effectiveId]
    const dateValue = normalizeDate(value)
    return formatter && dateValue ? formatter(dateValue) : null
  }

  if (axisMeta.kind === 'number') {
    const formatter = NUMBER_FORMATTERS[effectiveId]
    const numericValue = Number(value)
    return formatter && Number.isFinite(numericValue)
      ? formatter(numericValue)
      : null
  }

  return null
}

export function getAxisDisplayControls(mapping, dataTypes) {
  const xAxis = getAxisMeta('x', mapping, dataTypes)
  const yAxis = getAxisMeta('y', mapping, dataTypes)

  return {
    xAxis,
    yAxis,
    hasControls: Boolean(xAxis || yAxis),
  }
}

export function applyAxisDisplayFormatsToSvg(
  svgNode,
  { mapping, dataTypes, visualOptions, chartId = null } = {}
) {
  if (!svgNode || !mapping || !dataTypes || !visualOptions) return
  const resolvedChartId = chartId || visualOptions?.chartId || null

  const axes = [
    {
      id: 'xAxis',
      meta: getAxisMeta('x', mapping, dataTypes),
      formatId: visualOptions.xAxisDisplayFormat,
    },
    {
      id: 'yAxis',
      meta: getAxisMeta('y', mapping, dataTypes),
      formatId: visualOptions.yAxisDisplayFormat,
    },
  ]

  axes.forEach(({ id, meta, formatId: rawFormatId }) => {
    if (!meta) return
    // Default to 'auto' when the user hasn't set an explicit format
    const formatId = rawFormatId || 'auto'

    const axisNode = getAxisGroupNode(svgNode, id === 'xAxis' ? 'x' : 'y')
    if (!axisNode) return

    const ticks = Array.from(axisNode.querySelectorAll('.tick'))
    if (!ticks.length) return

    // For 'auto', detect the best format from tick values.
    // Prefer __data__ (set by D3); fall back to parsing the text content.
    let resolvedAutoId = null
    if (formatId === 'auto') {
      let tickValues = ticks.map(t => t.__data__).filter(v => v != null)
      if (!tickValues.length) {
        tickValues = ticks.map(t => {
          const raw = t.querySelector('text')?.textContent?.trim()
          if (!raw) return null
          const n = Number(raw.replace(/,/g, ''))
          return Number.isFinite(n) ? n : null
        }).filter(v => v != null)
      }
      if (!tickValues.length) return
      resolvedAutoId = meta.kind === 'date'
        ? detectAutoDateFormatId(tickValues)
        : detectAutoNumberFormatId(tickValues)
    }

    ticks.forEach((tickNode) => {
      const textNode = tickNode.querySelector('text')
      if (!textNode) return

      const formatted = formatTickValue(tickNode.__data__, meta, formatId, resolvedAutoId)
      if (formatted != null) {
        textNode.textContent = formatted
      }
    })
  })
}
